<template>
  <div class="AuxHUD FlexSplitter">
    <div class="AuxHUD_Group">
      <div
        v-if="hasWidget('StrokeColor')"
        class="AuxHUD_Item js-StrokeColorButton"
        v-on:click="openStrokeColorPicker"
        title="Stroke color"
      >
        <div class="ColorBox m-stroke" v-bind:style="strokeColorBoxStyle">
          <div
            class="ColorBox_Inner"
            v-bind:style="strokeColorBoxInnerStyle"
          ></div>
        </div>
      </div>

      <div
        v-if="hasWidget('FillColor')"
        class="AuxHUD_Item js-FillColorButton"
        v-on:click="openFillColorPicker"
        title="Fill color"
      >
        <div class="ColorBox m-fill" v-bind:style="fillColorBoxStyle">
          <div
            class="ColorBox_Inner"
            v-bind:style="fillColorBoxInnerStyle"
          ></div>
        </div>
      </div>

      <div
        v-if="hasWidget('FillColorForSticky')"
        class="AuxHUD_Item js-FillColorForStickyButton"
        v-on:click="openFillColorForStickyPicker"
        title="Sticky note color"
      >
        <div class="ColorBox m-fill-sticky" v-bind:style="fillColorBoxStyle">
          <div
            class="ColorBox_Inner"
            v-bind:style="fillColorForStickyBoxInnerStyle"
          ></div>
        </div>
      </div>

      <div
        v-if="hasWidget('StrokeWidth')"
        class="AuxHUD_Item js-StrokeWidthButton"
        v-on:click="openStrokeWidthPicker"
        title="Stroke width"
      >
        <div class="StrokeWidthIndicator">
          <div
            class="StrokeWidthIndicator_Circle"
            v-bind:style="strokeWidthCircleStyle"
          ></div>
        </div>
      </div>

      <div
        v-if="hasWidget('StrokeWidthForEraser')"
        class="AuxHUD_Item js-StrokeWidthButton"
        v-on:click="openStrokeWidthPicker"
        title="Stroke width"
      >
        <div class="StrokeWidthIndicator">
          <div
            class="StrokeWidthIndicator_Circle"
            v-bind:style="strokeWidthForEraserCircleStyle"
          ></div>
        </div>
      </div>

      <div
        v-if="hasWidget('DeleteSelection')"
        v-on:click="deleteSelection"
        v-on:touchstart="deleteSelection"
        title="Delete selected shapes"
        :class="{
          AuxHUD_Item: true,
          CircleShadowButton: true,
          'm-disabled': selections.length <= 0,
        }"
      >
        <img src="/img/icons/trash-outline.svg" />
      </div>

      <div
        v-if="hasWidget('DuplicateSelection')"
        v-on:click="duplicateSelectedShapes"
        v-on:touchstart="duplicateSelectedShapes"
        title="Duplicate selected shapes"
        :class="{
          AuxHUD_Item: true,
          CircleShadowButton: true,
          'm-disabled': selections.length <= 0,
        }"
      >
        <img src="/img/icons/copy-outline.svg" />
      </div>

      <OpacitySlider v-if="hasWidget('Opacity')"></OpacitySlider>

      <div class="QuillToolbarContainer" v-on:mousedown.prevent></div>
    </div>

    <div class="AuxHUD_Group">
      <div
        v-on:click="undo"
        v-on:touchstart="undo"
        title="Undo"
        :class="{
          CircleShadowButton: true,
          AuxHUD_Item: true,
          'm-undo': true,
          'm-disabled': !state.reported.undo.canUndo,
        }"
      >
        <img src="/img/icons/arrow-undo-outline.svg" />
      </div>

      <div
        v-on:click="redo"
        v-on:touchstart="redo"
        title="Redo"
        :class="{
          CircleShadowButton: true,
          AuxHUD_Item: true,
          'm-redo': true,
          'm-disabled': !state.reported.undo.canRedo,
        }"
      >
        <img src="/img/icons/arrow-redo-outline.svg" />
      </div>

      <div
        class="AuxHUD_Item CircleShadowButton m-warning m-variable-width"
        v-if="mayClear"
        v-on:click="clear"
        title="Delete everything"
      >
        <img src="/img/icons/delete-outline.svg" />
        <div class="CircleShadowButton_HoverText m-right">
          <span>Delete everything</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component } from "vue-property-decorator";
import tinycolor from "tinycolor2";

import { ModalType } from "@/store/ModalType";
import OpacitySlider from "./ui/OpacitySlider.vue";
import { WBSelection } from "@/wbcontroller/types/WBSelection";
import { getWidgetTypes, WidgetType } from "@/widgets";
import WBComponent from "@/WBComponent";
import { ModalMutations } from "@/store/modules/modal";

@Component({
  components: {
    OpacitySlider,
  },
})
export default class AuxHUD extends WBComponent {
  get mayClear(): boolean {
    return this.state.key.accessLevel === "admin";
  }

  get selections(): WBSelection[] {
    return this.state.selectedShapes;
  }

  get strokeWidth(): number {
    return this.state.hud.strokeWidth;
  }

  get strokeColorBoxStyle(): string {
    return "";
  }

  get strokeColorBoxInnerStyle(): string {
    return `border: 6px solid ${this.state.hud.strokeColor}`;
  }

  get fillColorBoxStyle(): string {
    return "";
  }

  get fillColorBoxInnerStyle(): string {
    if (tinycolor(this.state.hud.fillColor).getAlpha() < 1) {
      return `background-image: url("/img/checkerboard.png")`;
    } else {
      return `background-color: ${this.state.hud.fillColor}`;
    }
  }

  get fillColorForStickyBoxInnerStyle(): string {
    // Stickies can't have transparent background
    return `background-color: ${this.state.hud.fillColorForSticky}`;
  }

  get strokeWidthCircleStyle(): string {
    return `
      width: 26px;
      height: ${this.state.hud.strokeWidth}px;
      border-radius: ${this.state.hud.strokeWidth / 2}px;
      transform: rotate(-15deg);`;
  }

  get strokeWidthForEraserCircleStyle(): string {
    return `
      width: 26px;
      height: ${this.state.hud.strokeWidthForEraser}px;
      border-radius: ${this.state.hud.strokeWidthForEraser / 2}px;
      transform: rotate(-15deg);`;
  }

  hasWidget(w: string): boolean {
    return getWidgetTypes(
      this.state.hud.tool,
      this.state.selectedShapes.map((s) => s.shapeType)
    ).has(w as WidgetType);
  }

  openStrokeWidthPicker() {
    ModalMutations.updateModalAnchor(this.$store.commit, {
      modalType: ModalType.StrokeWidth,
      selector: ".js-StrokeWidthButton",
    });
    ModalMutations.openModal(this.$store.commit, ModalType.StrokeWidth);
  }

  openStrokeColorPicker() {
    ModalMutations.updateModalAnchor(this.$store.commit, {
      modalType: ModalType.StrokeColor,
      selector: ".js-StrokeColorButton",
    });
    ModalMutations.openModal(this.$store.commit, ModalType.StrokeColor);
  }

  openFillColorPicker() {
    ModalMutations.updateModalAnchor(this.$store.commit, {
      modalType: ModalType.FillColor,
      selector: ".js-FillColorButton",
    });
    ModalMutations.openModal(this.$store.commit, ModalType.FillColor);
  }

  openFillColorForStickyPicker() {
    ModalMutations.updateModalAnchor(this.$store.commit, {
      modalType: ModalType.FillColorForSticky,
      selector: ".js-FillColorForStickyButton",
    });
    ModalMutations.openModal(this.$store.commit, ModalType.FillColorForSticky);
  }

  deleteSelection(e: UIEvent) {
    e.stopPropagation();
    this.$emit("deleteSelection");
  }

  duplicateSelectedShapes() {
    this.$emit("duplicateSelectedShapes");
  }

  undo() {
    this.$emit("undo");
  }

  redo() {
    this.$emit("redo");
  }

  clear() {
    ModalMutations.openModal(
      this.$store.commit,
      ModalType.DeleteEverythingConfirmation
    );
  }
}
</script>

<style scoped lang="scss">
.AuxHUD {
  width: 100%;
  background-color: var(--bg-light);
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  border-bottom: 1px solid var(--separator-light);
  padding: 0 var(--margin-tiny);

  .AuxHUD_Group {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
  }
}

.AuxHUD_Item {
  width: 40px;
  height: 40px;
  margin: var(--margin-tiny) var(--margin-tiny) var(--margin-tiny) 0;
  background-color: var(--bg-light);

  &:first-child {
    margin-left: 0;
  }
}

.ColorBox {
  width: 40px;
  height: 40px;
  cursor: pointer;
  border: 1px solid var(--bg-dark);

  &:hover {
    border: 1px solid var(--button-primary-light);
  }

  &.m-fill {
    padding: 6px;
  }

  .ColorBox_Inner {
    width: 100%;
    height: 100%;
  }
}

.StrokeWidthIndicator {
  width: 40px;
  height: 40px;
  border: 2px solid var(--bg-dark);
  cursor: pointer;

  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    border-color: var(--highlight);
  }
}

.StrokeWidthIndicator_Circle {
  background-color: black;
}
</style>
